import { useContext } from "react";
import { Image } from "components/Image";
import { LOGOUT, menuItems } from "constants/general";
import { MenuContext } from "contexts/MenuContext";
import { CommonContext } from "contexts/CommonContext";
import {
  getUrlPathForMainMenu,
  pushToDataLayer,
} from "core/utils/utils.helper";
import { useNavigate } from "react-router-dom";
import type { MenuProps } from "antd";
import useAuth from "hooks/useAuth";
import expand from "assets/images/expand.png";
import collapse from "assets/images/collapse.png";
import { activeDateAtom } from "recLib/atoms/tracker";
import { useSetRecoilState } from "recoil";
import dayjs from "dayjs";
import { GERMAN_DATE_FORMAT } from "constants/date-time";
import styles from "./SideNavMenu.module.scss";
import { useModal } from "hooks/useModal";
import { TextModalContent } from "components/TextModalContent";
import { ModalType } from "models/common";
import { Typography } from "components/Typography";
import { CONTENT } from "constants/dataLayer";
import Layout from "antd/es/layout";
import Menu from "antd/es/menu";

const { Sider } = Layout;

export const SideNavMenu: React.FC = () => {
  const { collapsed, setCollapsed } = useContext(MenuContext);
  const { handleSignOut } = useContext(CommonContext); // signout function
  const selectedKey: any = getUrlPathForMainMenu(); // get selected menu from url
  const navigate = useNavigate();
  const auth: any = useAuth(); //oidc hook for handling authentication
  const setChangeActiveDate = useSetRecoilState(activeDateAtom); // change horizontal calender selected date to null when sidemenu clicked
  const isLoggedIn = auth?.isAuthenticated; // get login status from OIDC (IDHUB)
  const { showModal } = useModal(); // hook for modal component

  //menu array with all items
  const navItems: any[] = Object.entries(menuItems)
    .filter((e) => e[1].enabled) // filter enabled itemss
    .map((e) => e[1]);

  //route navigate
  const onClick: MenuProps["onClick"] = (e: any) => {
    let item = navItems.find((o: any) => o.key === e.key);
    let path = item && Object.keys(item).length !== 0 ? item.to : "";
    pushToDataLayer({
      ...CONTENT.MENU,
      section_name: item?.value,
      event_name: `cta_menu_${item?.key}`.toLowerCase(),
    });
    setChangeActiveDate(dayjs().startOf("day").format(GERMAN_DATE_FORMAT));
    path && navigate(item?.key === "home" && !isLoggedIn ? "/" : path);
  };

  const logOut = () => {
    // Logout functionality
    showModal({
      children: (
        <TextModalContent textContent={LOGOUT.HEADING} isIconVisible={true} />
      ),
      type: ModalType.NORMAL,
      onOk: () => handleSignOut(),
      okText: LOGOUT.BUTTON_OK,
      cancelText: LOGOUT.BUTTON_CANCEL,
      showFooter: true,
    });
  };
  return (
    <Sider
      width="21%"
      theme="light"
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={"auto"}
    >
      <div className={styles.sidebar_container}>
        <div
          className={`${styles.collapse_icon} ${collapsed ? styles.collapse_icon__expand : styles.collapse_icon__collapse}`}
          onClick={() => setCollapsed(!collapsed)}
          role="button"
          tabIndex={0}
          aria-label="collapse-icon"
        >
          {collapsed ? (
            <Image source={expand} altText="" />
          ) : (
            <Image source={collapse} altText="" />
          )}
        </div>
        {/* sidemenu */}
        <div
          className={`${styles.menu_wrapper} ${collapsed && styles.menu_wrapper__collapsed}`}
        >
          <Menu
            theme="light"
            mode="inline"
            className={`${styles.sidebar_wrapper}`}
            selectedKeys={[selectedKey]}
            onClick={onClick}
            role="menu"
          >
            {!collapsed && (
              <Typography
                variant="h3"
                className={styles.menu_heading}
                title="Navigation"
              >
                Navigation
              </Typography>
            )}
            {navItems?.map((item: any) => (
              <Menu.Item
                className={`${styles.menu_items} ${collapsed && styles.menu_items__collapse}`}
                key={item.key}
                title={collapsed ? item?.value : ""}
                role="menuitem"
              >
                <div className={styles.menu_items_inner}>
                  <Image
                    source={
                      selectedKey && selectedKey === item.key
                        ? item.iconActive
                        : item.icon
                    }
                    altText=""
                  />
                  {!collapsed ? (
                    <span
                      className={
                        selectedKey && selectedKey === item.key
                          ? styles.label_active
                          : ""
                      }
                    >
                      {item.value}
                    </span>
                  ) : (
                    ""
                  )}
                </div>
              </Menu.Item>
            ))}
          </Menu>
          {/* logout */}
          {!collapsed && isLoggedIn && (
            <div
              className={styles.menu_item_logout}
              onClick={() => logOut()}
              role="button"
              tabIndex={0}
              aria-label="logout-button"
            >
              Logout
            </div>
          )}
        </div>
      </div>
    </Sider>
  );
};
